import { useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import usePageMeta, { SITE_NAME } from "@/hooks/usePageMeta";

const features = [
  {
    icon: "ri-book-2-line",
    title: "海量韩漫资源",
    desc: "覆盖爱情、奇幻、悬疑、校园等多种热门题材，持续更新，满足不同的阅读口味。",
  },
  {
    icon: "ri-shield-check-line",
    title: "无删减完整内容",
    desc: "主打无删减漫画内容，保障故事情节的完整性，让每一次阅读都原汁原味。",
  },
  {
    icon: "ri-arrow-down-double-line",
    title: "便捷下拉阅读",
    desc: "支持下拉式连续阅读，滚动到底自动加载后续内容，追漫流畅不打断。",
  },
  {
    icon: "ri-heart-3-line",
    title: "本地收藏与进度",
    desc: "收藏喜欢的作品、记录阅读进度，数据保存在本地，下次打开继续阅读。",
  },
];

const FORM_SUBMIT = "https://readdy.ai/api/form/db6fmm3ghk82orf0cl40";

export default function AboutPage() {
  usePageMeta(
    `关于我们 - ${SITE_NAME}`,
    "了解韩漫大全无删减免费阅读下拉平台定位、内容特色、联系方式与版权声明，专注为韩漫爱好者提供便捷的在线阅读体验。",
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("company_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交被识别为异常内容，请调整后重试。"
            : serverMsg || "提交失败，请稍后再试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-14">
        <div className="max-w-4xl mx-auto">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
            <i className="ri-information-line"></i>
            关于我们
          </span>
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
            韩漫大全无删减免费阅读下拉
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-4 leading-relaxed">
            我们致力于为韩漫爱好者打造一个资源丰富、持续更新的在线阅读平台。平台覆盖爱情、奇幻、悬疑、校园等多种热门题材，主打无删减内容，保障阅读的完整性，让每一位用户都能便捷、顺畅地享受追漫乐趣。
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-5 mt-8">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="p-5 rounded-lg bg-background-100 border border-background-200"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 text-2xl mb-3">
                  <i className={feature.icon}></i>
                </span>
                <h2 className="font-heading font-semibold text-base text-foreground-950">
                  {feature.title}
                </h2>
                <p className="text-sm text-foreground-600 mt-2 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>

          <section className="mt-10 p-5 md:p-6 rounded-lg bg-secondary-50 border border-secondary-200">
            <h2 className="flex items-center gap-2 font-heading font-semibold text-lg text-secondary-800">
              <i className="ri-alert-line"></i>
              演示说明与版权声明
            </h2>
            <p className="text-sm text-secondary-900/80 mt-3 leading-relaxed">
              本站为前端演示站点，所有漫画封面、简介、搜索与分页均为模拟数据，仅用于展示阅读体验与功能流程，不提供任何真实漫画内容。
            </p>
            <p className="text-sm text-secondary-900/80 mt-3 leading-relaxed">
              本站尊重并重视版权，所有演示素材仅用于功能展示。如需浏览真实作品，请前往正规正版平台观看，支持创作者与正版内容。
            </p>
          </section>

          <section className="mt-10">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              有任何建议、反馈或合作意向，欢迎通过留言表单联系我们，我们会尽快回复。
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 text-xl shrink-0">
                    <i className="ri-mail-line"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">联系邮箱</p>
                    <p className="text-sm text-foreground-900 mt-0.5 break-all">
                      support@hanmandownload.demo
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                  <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 text-xl shrink-0">
                    <i className="ri-time-line"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">反馈时间</p>
                    <p className="text-sm text-foreground-900 mt-0.5">
                      工作日 09:00 - 18:00
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
                <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-foreground-700 mb-1.5">
                        你的称呼
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="请输入称呼"
                        className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-200"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="请输入邮箱，方便回复"
                        className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-200"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请输入你的建议或反馈（最多 500 字）"
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-200 resize-none"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-field-captcha-guard"
                  />

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer"
                  >
                    <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                    {status === "submitting" ? "提交中…" : "提交留言"}
                  </button>

                  {status === "success" && (
                    <p className="flex items-center gap-2 text-sm text-secondary-700 bg-secondary-50 border border-secondary-200 rounded-md px-3 py-2.5">
                      <i className="ri-checkbox-circle-line"></i>
                      感谢你的留言，我们已经收到，会尽快回复你。
                    </p>
                  )}
                  {status === "error" && (
                    <p className="flex items-center gap-2 text-sm text-accent-800 bg-accent-50 border border-accent-200 rounded-md px-3 py-2.5">
                      <i className="ri-error-warning-line"></i>
                      {formError}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>
    </SiteLayout>
  );
}